import { useEffect, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import RhfInput from "@/components/rhfFields/RhfInput";
import RhfRadio from "@/components/rhfFields/RhfRadio";
import RhfSelect from "@/components/rhfFields/RhfSelect";
import {
  TCM_COMPLEXITY_OPTIONS,
  TCM_DISCHARGED_FROM_OPTIONS,
  TCM_DISCHARGED_TO_OPTIONS,
  TCM_ICD_OPTIONS,
  TCM_PLAN_STATUS_OPTIONS,
  TCM_TELEHEALTH_STATUS_OPTIONS,
} from "./tcmPlanDummy";

function FieldHint({ children }: { children: ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

function requiredByLabel(dischargeDate: string, complexity: string) {
  if (!dischargeDate) return "Required by the face-to-face window after discharge.";
  const days = complexity === "high" ? 7 : 14;
  const date = new Date(`${dischargeDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "Required by the face-to-face window after discharge.";
  date.setDate(date.getDate() + days);
  return `Required by: ${date.toLocaleDateString("en-US")} (Day ${days})`;
}

export function TcmAutoStatus({
  hasUpcomingAppointment,
  isCaretaker,
}: {
  hasUpcomingAppointment: boolean;
  isCaretaker: boolean;
}) {
  const { setValue, watch } = useFormContext();
  const dischargeDate = watch("dischargeDate");
  const planStatus = watch("planStatus");
  const telehealthStatus = watch("telehealthStatus");

  useEffect(() => {
    if (!dischargeDate || (planStatus && planStatus !== "in_progress")) return;
    const discharged = new Date(`${dischargeDate}T00:00:00`);
    if (Number.isNaN(discharged.getTime())) return;
    const nonCompliantOn = new Date(discharged);
    nonCompliantOn.setDate(nonCompliantOn.getDate() + 30);
    if (Date.now() >= nonCompliantOn.getTime()) {
      setValue("planStatus", "non_compliant");
    }
  }, [dischargeDate, planStatus, setValue]);

  useEffect(() => {
    if (hasUpcomingAppointment && telehealthStatus === "pending") {
      setValue("telehealthStatus", "scheduled");
    }
    if (!isCaretaker && telehealthStatus === "completed") {
      setValue("telehealthStatus", hasUpcomingAppointment ? "scheduled" : "pending");
    }
  }, [hasUpcomingAppointment, isCaretaker, setValue, telehealthStatus]);

  return null;
}

export function TcmPlanDetailsCard({ isCaretaker }: { isCaretaker: boolean }) {
  const { watch } = useFormContext();
  const dischargeDate = watch("dischargeDate");
  const complexity = watch("complexity");
  const telehealthOptions = TCM_TELEHEALTH_STATUS_OPTIONS.map((option) => ({
    ...option,
    disabled: option.value === "completed" && !isCaretaker,
  }));

  return (
    <section className="space-y-4 rounded-lg border bg-white p-4 md:space-y-8">
      <div className="grid gap-4 lg:grid-cols-3">
        <RhfInput name="dischargeDate" label="Discharge Date" type="date" required />
        <RhfSelect
          name="dischargedFrom"
          label="Discharged From"
          required
          options={TCM_DISCHARGED_FROM_OPTIONS}
          placeholder="Select setting"
        />
        <RhfSelect
          name="dischargedTo"
          label="Discharged To"
          required
          options={TCM_DISCHARGED_TO_OPTIONS}
          placeholder="Select setting"
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-4 md:gap-6">
        
        <div className="space-y-1">
          <RhfSelect
            name="complexity"
            label="Complexity Level (Medical Decision Making)"
            required
            options={TCM_COMPLEXITY_OPTIONS}
          />
          <FieldHint>{requiredByLabel(String(dischargeDate ?? ""), String(complexity ?? ""))}</FieldHint>
        </div>
        <div className="space-y-1">
          <RhfSelect
            name="planStatus"
            label="TCM Plan Status"
            required
            options={TCM_PLAN_STATUS_OPTIONS}
          />
          <FieldHint>
            Auto-marked <span className="font-medium text-red-600">Non-Compliant</span> after 30 days of discharge
          </FieldHint>
        </div>
        <div className="space-y-1">
          <RhfSelect
            name="telehealthStatus"
            label="Telehealth Status"
            required
            options={telehealthOptions}
          />
          <FieldHint>Auto-marked Scheduled when appointment exists</FieldHint>
        </div>
        <div className="space-y-1">
          <RhfRadio
            name="twoDayReachout"
            label="2-Day Reachout Completed?"
            options={[
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
            ]}
          />
          <FieldHint>Required contact within 48h of discharge</FieldHint>
        </div>
      </div>
      {/* <div className="mt-4 grid gap-4 md:grid-cols-2">
        <RhfInput name="dischargeFacility" label="Discharge Facility" required />
        <RhfSelect
          name="icdCodes"
          label="ICD Codes"
          required
          multiple
          options={TCM_ICD_OPTIONS}
          placeholder="Select ICD codes"
        />
      </div> */}
    </section>
  );
}
